import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Send, ChevronDown, ChevronRight, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { statusLabel } from '@/lib/deLabels';
import EmailPreviewConfirm from '../email/EmailPreviewConfirm';

const ID_KEYS = { guardian: 'guardian_id', student: 'student_id', teacher: 'teacher_id', family: 'family_id' };
const IDS_KEYS = { guardian: 'guardian_ids', student: 'student_ids', teacher: 'teacher_ids', family: 'family_ids' };

function targetPayload(mode, selected, list) {
  if (mode === 'bulk') {
    return { [IDS_KEYS[list?.kind] || 'guardian_ids']: list?.ids || [] };
  }
  return { [ID_KEYS[selected?.kind] || 'guardian_id']: selected?.id };
}

function ChannelToggles({ value, onChange, locked }) {
  const toggle = (key) => {
    if (key === locked) return;
    onChange({ ...value, [key]: !value[key] });
  };
  const box = (key, label, testId) => (
    <label
      className={`flex items-center gap-2 text-sm cursor-pointer ${key === locked ? 'font-medium text-slate-900' : 'text-slate-700'}`}
    >
      <input
        type="checkbox"
        checked={!!value[key]}
        disabled={key === locked}
        onChange={() => toggle(key)}
        className="h-4 w-4 rounded border-slate-300 text-brand-red"
        data-testid={testId}
      />
      {label}{key === locked ? ' (dieser Versand)' : ''}
    </label>
  );
  return (
    <div className="flex flex-wrap gap-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="actionbar-channels">
      {box('app', 'App-Nachricht', 'actionbar-channel-app')}
      {box('email', 'E-Mail', 'actionbar-channel-email')}
      {box('sms', 'SMS', 'actionbar-channel-sms')}
    </div>
  );
}

async function sendAppAlongside(mode, selected, list, { subject, text }) {
  const path = mode === 'bulk' ? '/correspondence/send-app-bulk' : '/correspondence/send-app';
  const r = await api.post(path, { ...targetPayload(mode, selected, list), subject, text });
  return r.data;
}

export function SendEmailDialog({ open, onClose, mode, freeText, selected, list }) {
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [bulkStats, setBulkStats] = useState(null);
  const [mailPreview, setMailPreview] = useState(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const [alsoApp, setAlsoApp] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setSubject(''); setBody(''); setTemplateId(''); setBulkStats(null); setShowSkipped(false); setMailPreview(null); setAlsoApp(false);
    if (!freeText) api.get('/correspondence/templates').then((r) => setTemplates(r.data)).catch(() => {});
    api.get('/correspondence/placeholders').then((r) => setPlaceholders(r.data)).catch(() => {});
    if (mode === 'bulk') {
      api.post('/correspondence/send-email-bulk', { dry_run: true, ...targetPayload('bulk', selected, list) })
        .then((r) => setBulkStats(r.data))
        .catch((e) => toast.error(e.response?.data?.detail || 'Vorschau fehlgeschlagen'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) { if (t.subject) setSubject(t.subject); if (t.body) setBody(t.body); }
  };

  const insertPlaceholder = (key) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + key); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + key + body.slice(end));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + key.length; });
  };

  const openContentPreview = async () => {
    if (!body.trim()) { toast.error('Bitte einen Text eingeben'); return; }
    setPreviewing(true);
    try {
      const r = await api.post('/correspondence/preview-email', {
        ...targetPayload(mode, selected, list),
        subject,
        body,
        template_id: templateId || undefined,
      });
      setMailPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Voransicht fehlgeschlagen');
    } finally {
      setPreviewing(false);
    }
  };

  const send = async (routing = {}) => {
    if (!mailPreview?.preview_token) { toast.error('Bitte zuerst die Voransicht öffnen'); return; }
    setSending(true);
    try {
      if (mode === 'bulk') {
        const r = await api.post('/correspondence/send-email-bulk', {
          ...targetPayload('bulk', selected, list),
          subject,
          body,
          template_id: templateId || undefined,
          preview_token: mailPreview.preview_token,
          ...routing,
        });
        toast.success(r.data.message || `Sammelmail für ${r.data.count} Empfänger gestartet`);
      } else {
        const r = await api.post('/correspondence/send-email', {
          ...targetPayload('single', selected, list),
          subject,
          body,
          template_id: templateId || undefined,
          preview_token: mailPreview.preview_token,
          ...routing,
        });
        toast.success(r.data.queued_for_approval
          ? `E-Mail an ${r.data.recipient} eingereiht (wartet auf Freigabe)`
          : `E-Mail an ${r.data.recipient} gesendet`);
      }
      if (alsoApp) {
        try {
          const appRes = await sendAppAlongside(mode, selected, list, { subject, text: body });
          toast.success(appRes.message || (appRes.sent ? `App-Nachricht an ${appRes.sent} Empfänger` : 'App-Nachricht gesendet'));
        } catch (appErr) {
          toast.error(appErr.response?.data?.detail || 'E-Mail gesendet, App-Nachricht fehlgeschlagen');
        }
      }
      setMailPreview(null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally { setSending(false); }
  };

  const title = mode === 'bulk' ? 'Sammelmail aus Korrespondenz' : (freeText ? 'E-Mail (Freitext)' : 'Einzelmail aus Korrespondenz');

  return (
    <>
    <Dialog open={open && !mailPreview} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="actionbar-email-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === 'bulk'
              ? `An alle aufgerufenen Datensätze (${list?.ids?.length || 0}) — Versand nur nach HTML-Voransicht`
              : <>Empfänger: <span className="font-medium text-slate-700">{selected?.name}{selected?.email ? ` · ${selected.email}` : ''}</span></>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {mode === 'bulk' && bulkStats && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="actionbar-email-bulk-stats">
              <div className="font-medium text-slate-900">
                {bulkStats.eligible === 1 ? '1 E-Mail wird erstellt' : `${bulkStats.eligible} E-Mails werden erstellt`}
              </div>
              {bulkStats.skipped?.length > 0 && (
                <div className="mt-1">
                  <button onClick={() => setShowSkipped((v) => !v)} className="flex items-center gap-1 text-xs text-amber-700 font-medium" data-testid="actionbar-email-skipped-toggle">
                    {showSkipped ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    <AlertTriangle className="h-3.5 w-3.5" /> {bulkStats.skipped.length} übersprungen
                  </button>
                  {showSkipped && (
                    <div className="mt-1 max-h-32 overflow-y-auto divide-y divide-slate-200 text-xs">
                      {bulkStats.skipped.map((s) => (
                        <div key={s.id} className="flex justify-between gap-3 py-1"><span>{s.name}</span><span className="text-slate-500">{s.reason}</span></div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {!freeText && (
            <div>
              <label className="text-xs font-medium text-slate-500">Textvorlage (Korrespondenz)</label>
              <Select value={templateId} onValueChange={applyTemplate}>
                <SelectTrigger className="mt-1" data-testid="actionbar-email-template-select">
                  <SelectValue placeholder="Vorlage wählen…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.is_favorite ? '★ ' : ''}{t.module} · {t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-500">Betreff</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="actionbar-email-subject" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Text</label>
            <Textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="mt-1 font-mono text-xs" data-testid="actionbar-email-body" />
            <div className="flex flex-wrap gap-1 mt-2">
              {placeholders.map((p) => (
                <button key={p.key} onClick={() => insertPlaceholder(p.key)} title={p.label}
                  className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-brand-red/10 hover:text-brand-red font-mono transition-colors">
                  {p.key}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Zusätzlich senden über</label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={alsoApp}
                onChange={(e) => setAlsoApp(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-red"
                data-testid="actionbar-email-also-app"
              />
              Auch als App-Nachricht
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="actionbar-email-cancel">Abbrechen</Button>
            <Button
              onClick={openContentPreview}
              disabled={previewing || (mode === 'bulk' && !bulkStats?.eligible)}
              data-testid="actionbar-email-send"
            >
              {previewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
              Voransicht
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <EmailPreviewConfirm
      open={!!mailPreview}
      onClose={() => setMailPreview(null)}
      onConfirm={send}
      confirming={sending}
      title={mode === 'bulk' ? 'Sammelmail — Voransicht' : 'E-Mail — Voransicht'}
      description="Bitte Inhalt prüfen. Versand erst nach „Jetzt senden“."
      to={mailPreview?.recipient}
      toLabel={mailPreview?.recipient_name}
      recipients={mailPreview?.recipients}
      subject={mailPreview?.subject}
      html={mailPreview?.html}
      sampleNote={mailPreview?.is_sample
        ? `Beispiel für 1 von ${mailPreview.eligible_count} Empfängern (Platzhalter werden je Empfänger individuell ersetzt).`
        : null}
      confirmLabel={mode === 'bulk'
        ? `An ${mailPreview?.eligible_count ?? 0} Empfänger senden`
        : 'Jetzt senden'}
      testId="actionbar-email-preview"
    />
    </>
  );
}

export function SendSmsDialog({ open, onClose, selected, list }) {
  const [scope, setScope] = useState('single');
  const [text, setText] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [alsoApp, setAlsoApp] = useState(false);
  const bodyRef = useRef(null);

  const canSingle = !!selected;
  const canBulk = (list?.ids?.length || 0) > 0;

  useEffect(() => {
    if (!open) return;
    setText(''); setPreview(null); setAlsoApp(false);
    setScope(canSingle ? 'single' : 'bulk');
    api.get('/correspondence/placeholders').then((r) => setPlaceholders(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || scope !== 'bulk') { setPreview(null); return; }
    api.post('/correspondence/send-sms-bulk', { dry_run: true, ...targetPayload('bulk', selected, list) })
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Vorschau fehlgeschlagen'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scope, list?.ids?.length]);

  const insertPlaceholder = (key) => {
    const el = bodyRef.current;
    if (!el) { setText((b) => b + key); return; }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    setText(text.slice(0, start) + key + text.slice(end));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + key.length; });
  };

  const send = async () => {
    if (!text.trim()) { toast.error('Bitte einen SMS-Text eingeben'); return; }
    setSending(true);
    try {
      if (scope === 'bulk') {
        const r = await api.post('/correspondence/send-sms-bulk', { ...targetPayload('bulk', selected, list), text });
        toast.success(r.data.message || `Sammel-SMS für ${r.data.count} Empfänger gestartet`);
      } else {
        const r = await api.post('/correspondence/send-sms', { ...targetPayload('single', selected, list), text });
        toast.success(`SMS an ${r.data.recipient} versendet (${statusLabel(r.data.status)})`);
      }
      if (alsoApp) {
        try {
          const appRes = await sendAppAlongside(scope, selected, list, { text });
          toast.success(appRes.message || (appRes.sent ? `App-Nachricht an ${appRes.sent} Empfänger` : 'App-Nachricht gesendet'));
        } catch (appErr) {
          toast.error(appErr.response?.data?.detail || 'SMS gesendet, App-Nachricht fehlgeschlagen');
        }
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SMS-Versand fehlgeschlagen');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="actionbar-sms-dialog">
        <DialogHeader>
          <DialogTitle>SMS versenden</DialogTitle>
          <DialogDescription>Freitext mit Platzhaltern – Versand über das SMS-Kontingent (Bird).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button onClick={() => canSingle && setScope('single')} disabled={!canSingle}
              className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${scope === 'single' ? 'border-brand-red/40 bg-brand-red/5 text-brand-red' : 'border-slate-200 text-slate-600 hover:bg-slate-50'} ${!canSingle ? 'opacity-40' : ''}`}
              data-testid="actionbar-sms-scope-single">
              Einzeln{selected ? `: ${selected.name}` : ''}
            </button>
            <button onClick={() => canBulk && setScope('bulk')} disabled={!canBulk}
              className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${scope === 'bulk' ? 'border-brand-red/40 bg-brand-red/5 text-brand-red' : 'border-slate-200 text-slate-600 hover:bg-slate-50'} ${!canBulk ? 'opacity-40' : ''}`}
              data-testid="actionbar-sms-scope-bulk">
              Alle aufgerufenen ({list?.ids?.length || 0})
            </button>
          </div>
          {scope === 'bulk' && preview && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="actionbar-sms-bulk-stats">
              <b>{preview.eligible}</b> SMS werden versendet (Kontingent!)
              {preview.skipped?.length > 0 && <> · {preview.skipped.length} ohne Nummer übersprungen</>}
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-500">SMS-Text</label>
            <Textarea ref={bodyRef} value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={480} className="mt-1 font-mono text-xs" data-testid="actionbar-sms-text" />
            <div className="flex items-center justify-between mt-1">
              <div className="flex flex-wrap gap-1">
                {placeholders.slice(0, 6).map((p) => (
                  <button key={p.key} onClick={() => insertPlaceholder(p.key)} title={p.label}
                    className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-brand-red/10 hover:text-brand-red font-mono transition-colors">
                    {p.key}
                  </button>
                ))}
              </div>
              <span className={`text-[11px] ${text.length > 160 ? 'text-brand-red font-medium' : 'text-slate-400'}`} data-testid="actionbar-sms-charcount">
                {text.length}/160{text.length > 160 ? ` (${Math.ceil(text.length / 160)} SMS)` : ''}
              </span>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={alsoApp}
              onChange={(e) => setAlsoApp(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-red"
              data-testid="actionbar-sms-also-app"
            />
            Auch als App-Nachricht
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} data-testid="actionbar-sms-cancel">Abbrechen</Button>
            <Button onClick={send} disabled={sending || (scope === 'bulk' && !preview?.eligible)} data-testid="actionbar-sms-send">
              {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              {scope === 'bulk' ? `An ${preview?.eligible ?? 0} Empfänger senden` : 'SMS senden'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SendAppDialog({ open, onClose, selected, list }) {
  const [scope, setScope] = useState('single');
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [channels, setChannels] = useState({ app: true, email: false, sms: false });
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [mailPreview, setMailPreview] = useState(null);
  const bodyRef = useRef(null);

  const canSingle = !!selected;
  const canBulk = (list?.ids?.length || 0) > 0;

  useEffect(() => {
    if (!open) return;
    setSubject(''); setText(''); setPreview(null); setMailPreview(null);
    setChannels({ app: true, email: false, sms: false });
    setScope(canSingle ? 'single' : 'bulk');
    api.get('/correspondence/placeholders').then((r) => setPlaceholders(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) { setPreview(null); return; }
    const mode = scope === 'bulk' ? 'bulk' : 'single';
    if (scope !== 'bulk') { setPreview(null); return; }
    const payload = { dry_run: true, ...targetPayload('bulk', selected, list) };
    Promise.all([
      api.post('/correspondence/send-app-bulk', payload).catch(() => ({ data: null })),
      channels.sms ? api.post('/correspondence/send-sms-bulk', payload).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      channels.email ? api.post('/correspondence/send-email-bulk', payload).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ]).then(([appR, smsR, emailR]) => {
      setPreview({
        app: appR.data,
        sms: smsR.data,
        email: emailR.data,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scope, list?.ids?.length, channels.sms, channels.email]);

  const insertPlaceholder = (key) => {
    const el = bodyRef.current;
    if (!el) { setText((b) => b + key); return; }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    setText(text.slice(0, start) + key + text.slice(end));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + key.length; });
  };

  const sendSelectedChannels = async (previewToken, routing = {}) => {
    const mode = scope;
    const payload = { ...targetPayload(mode, selected, list), subject, text, body: text };
    const results = [];
    if (channels.app) {
      const path = mode === 'bulk' ? '/correspondence/send-app-bulk' : '/correspondence/send-app';
      const r = await api.post(path, payload);
      results.push(r.data.message || (r.data.sent ? `App: ${r.data.sent}` : 'App-Nachricht gesendet'));
    }
    if (channels.sms) {
      const path = mode === 'bulk' ? '/correspondence/send-sms-bulk' : '/correspondence/send-sms';
      const r = await api.post(path, payload);
      results.push(r.data.message || `SMS: ${r.data.status || r.data.count || 'gesendet'}`);
    }
    if (channels.email) {
      const path = mode === 'bulk' ? '/correspondence/send-email-bulk' : '/correspondence/send-email';
      const r = await api.post(path, { ...payload, preview_token: previewToken, ...routing });
      results.push(r.data.message || `E-Mail an ${r.data.recipient || r.data.count || 'Empfänger'}`);
    }
    results.forEach((msg) => toast.success(msg));
    setMailPreview(null);
    onClose();
  };

  const send = async () => {
    if (!text.trim()) { toast.error('Bitte einen Text eingeben'); return; }
    if (!channels.app && !channels.email && !channels.sms) {
      toast.error('Bitte mindestens einen Kanal wählen');
      return;
    }
    if (channels.email && !mailPreview?.preview_token) {
      setPreviewing(true);
      try {
        const r = await api.post('/correspondence/preview-email', {
          ...targetPayload(scope, selected, list),
          subject,
          body: text,
        });
        setMailPreview(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'E-Mail-Voransicht fehlgeschlagen');
      } finally {
        setPreviewing(false);
      }
      return;
    }
    setSending(true);
    try {
      await sendSelectedChannels(mailPreview?.preview_token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally { setSending(false); }
  };

  const appEligible = preview?.app?.eligible;
  const canSendBulk = scope !== 'bulk' || (appEligible > 0) || (channels.sms && preview?.sms?.eligible) || (channels.email && preview?.email?.eligible);

  return (
    <>
    <Dialog open={open && !mailPreview} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="actionbar-app-dialog">
        <DialogHeader>
          <DialogTitle>App-Nachricht versenden</DialogTitle>
          <DialogDescription>
            Die Nachricht erscheint in der App (Nachrichten) und als Push, sofern die Person ein App-Konto hat.
            Zusätzlich E-Mail und SMS möglich.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button onClick={() => canSingle && setScope('single')} disabled={!canSingle}
              className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${scope === 'single' ? 'border-brand-red/40 bg-brand-red/5 text-brand-red' : 'border-slate-200 text-slate-600 hover:bg-slate-50'} ${!canSingle ? 'opacity-40' : ''}`}
              data-testid="actionbar-app-scope-single">
              Einzeln{selected ? `: ${selected.name}` : ''}
            </button>
            <button onClick={() => canBulk && setScope('bulk')} disabled={!canBulk}
              className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${scope === 'bulk' ? 'border-brand-red/40 bg-brand-red/5 text-brand-red' : 'border-slate-200 text-slate-600 hover:bg-slate-50'} ${!canBulk ? 'opacity-40' : ''}`}
              data-testid="actionbar-app-scope-bulk">
              Alle aufgerufenen ({list?.ids?.length || 0})
            </button>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Kanäle</label>
            <ChannelToggles value={channels} onChange={setChannels} locked="app" />
          </div>
          {scope === 'bulk' && preview?.app && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-700" data-testid="actionbar-app-bulk-stats">
              <b>{preview.app.eligible}</b> mit App-Konto
              {preview.app.skipped?.length > 0 && <> · {preview.app.skipped.length} ohne App übersprungen</>}
              {channels.sms && preview.sms && <> · {preview.sms.eligible} SMS</>}
              {channels.email && preview.email && <> · {preview.email.eligible} E-Mails</>}
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-500">Betreff (optional, für Push und E-Mail)</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="actionbar-app-subject" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Text</label>
            <Textarea ref={bodyRef} value={text} onChange={(e) => setText(e.target.value)} rows={6} className="mt-1 font-mono text-xs" data-testid="actionbar-app-text" />
            <div className="flex flex-wrap gap-1 mt-2">
              {placeholders.slice(0, 8).map((p) => (
                <button key={p.key} onClick={() => insertPlaceholder(p.key)} title={p.label}
                  className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-brand-red/10 hover:text-brand-red font-mono transition-colors">
                  {p.key}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} data-testid="actionbar-app-cancel">Abbrechen</Button>
            <Button
              onClick={send}
              disabled={sending || previewing || (scope === 'bulk' && !canSendBulk)}
              data-testid="actionbar-app-send"
            >
              {(sending || previewing) ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              {channels.email && !mailPreview ? 'Voransicht' : (scope === 'bulk' ? `Senden (${preview?.app?.eligible ?? 0})` : 'Senden')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <EmailPreviewConfirm
      open={!!mailPreview}
      onClose={() => setMailPreview(null)}
      onConfirm={async (routing) => {
        setSending(true);
        try {
          await sendSelectedChannels(mailPreview?.preview_token, routing);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
        } finally { setSending(false); }
      }}
      confirming={sending}
      title="E-Mail — Voransicht"
      description="App-Nachricht und ggf. SMS werden zusammen mit der E-Mail versendet."
      to={mailPreview?.recipient}
      toLabel={mailPreview?.recipient_name}
      recipients={mailPreview?.recipients}
      subject={mailPreview?.subject}
      html={mailPreview?.html}
      confirmLabel="Jetzt senden"
      testId="actionbar-app-email-preview"
    />
    </>
  );
}

export function PrintLetterDialog({ open, onClose, selected, list }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [scope, setScope] = useState('single');
  const [onlyWithoutEmail, setOnlyWithoutEmail] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const canSingle = !!selected;
  const canBulk = (list?.ids?.length || 0) > 0;

  useEffect(() => {
    if (!open) return;
    setTemplateId('');
    setScope(canSingle ? 'single' : 'bulk');
    setOnlyWithoutEmail(false);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    api.get('/correspondence/templates').then((r) => setTemplates(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) return;
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const generate = async () => {
    if (!templateId) { toast.error('Bitte eine Vorlage wählen'); return; }
    setGenerating(true);
    try {
      const isBulk = scope === 'bulk';
      const r = await api.post(
        isBulk ? '/correspondence/letter-pdf-bulk' : '/correspondence/letter-pdf',
        {
          template_id: templateId,
          ...targetPayload(isBulk ? 'bulk' : 'single', selected, list),
          ...(isBulk ? { only_without_email: onlyWithoutEmail } : {}),
        },
        { responseType: 'blob' });
      setPdfUrl(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })));
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try {
          const d = JSON.parse(await e.response.data.text());
          toast.error(d.detail || 'Brief konnte nicht erstellt werden');
        } catch { toast.error('Brief konnte nicht erstellt werden'); }
      } else {
        toast.error('Brief konnte nicht erstellt werden');
      }
    } finally { setGenerating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { if (pdfUrl) URL.revokeObjectURL(pdfUrl); onClose(); } }}>
      <DialogContent className={pdfUrl ? 'max-w-4xl h-[88vh] flex flex-col' : 'max-w-md'} data-testid="actionbar-print-dialog">
        <DialogHeader>
          <DialogTitle>{scope === 'bulk' ? 'Serienbrief drucken' : 'Brief drucken'}</DialogTitle>
          <DialogDescription>
            {scope === 'bulk'
              ? 'Ein PDF mit einem Brief pro Empfänger – z. B. für den Postversand.'
              : <>Empfänger: <span className="font-medium text-slate-700">{selected?.name}</span> – mit Briefkopf und Adresse.</>}
          </DialogDescription>
        </DialogHeader>
        {pdfUrl ? (
          <>
            <iframe src={pdfUrl} title="Brief" className="flex-1 w-full rounded border border-slate-200" data-testid="actionbar-print-iframe" />
            <div className="flex justify-end pt-2">
              <Button onClick={() => { const a = document.createElement('a'); a.href = pdfUrl; a.download = scope === 'bulk' ? 'Serienbrief.pdf' : `Brief_${selected?.name || 'Empfaenger'}.pdf`; a.click(); }} data-testid="actionbar-print-download">
                PDF herunterladen
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <button onClick={() => canSingle && setScope('single')} disabled={!canSingle}
                className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${scope === 'single' ? 'border-brand-red/40 bg-brand-red/5 text-brand-red' : 'border-slate-200 text-slate-600 hover:bg-slate-50'} ${!canSingle ? 'opacity-40' : ''}`}
                data-testid="actionbar-print-scope-single">
                Einzelbrief{selected ? `: ${selected.name}` : ''}
              </button>
              <button onClick={() => canBulk && setScope('bulk')} disabled={!canBulk}
                className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${scope === 'bulk' ? 'border-brand-red/40 bg-brand-red/5 text-brand-red' : 'border-slate-200 text-slate-600 hover:bg-slate-50'} ${!canBulk ? 'opacity-40' : ''}`}
                data-testid="actionbar-print-scope-bulk">
                Serienbrief: alle aufgerufenen ({list?.ids?.length || 0})
              </button>
            </div>
            {scope === 'bulk' && (
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="actionbar-print-without-email">
                <input type="checkbox" checked={onlyWithoutEmail} onChange={(e) => setOnlyWithoutEmail(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-brand-red" />
                Nur Empfänger <b>ohne E-Mail-Adresse</b> (für Postversand)
              </label>
            )}
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="actionbar-print-template-select"><SelectValue placeholder="Vorlage wählen…" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.is_favorite ? '★ ' : ''}{t.module} · {t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Abbrechen</Button>
              <Button onClick={generate} disabled={generating || !templateId} data-testid="actionbar-print-generate">
                {generating && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                {scope === 'bulk' ? `Serienbrief erstellen (${list?.ids?.length || 0})` : 'Brief erstellen'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
